import { useUrlSearchParams } from '@dai/common'
import styled from '@emotion/styled'
import {
  Autocomplete as MuiAutocomplete,
  AutocompleteRenderOptionState,
  Checkbox,
  ListItem,
  Radio,
  TextField,
  Typography,
} from '@mui/material'
import { isArray } from 'lodash'
import { useEffect, useState } from 'react'

import useT from '@/i18n/useT'

const Autocomplete = styled(MuiAutocomplete)({
  '& .MuiAutocomplete-inputRoot': {
    flexWrap: 'nowrap',
  },
}) as typeof MuiAutocomplete

const AutocompleteWithWrap = styled(MuiAutocomplete)({
  '& .MuiAutocomplete-inputRoot': {
    flexWrap: 'wrap',
  },
}) as typeof MuiAutocomplete

type AutocompleteWithUrlParamsProps = {
  urlKey: string
  multiselect: boolean
  options: { label: string; value: string }[]
  label: string
  loading: boolean
  wrapTags?: boolean
}
export const AutocompleteWithUrlParams: React.FC<
  AutocompleteWithUrlParamsProps
> = props => {
  const {
    state: urlState,
    handle: { handleUpdateSearchParams },
  } = useUrlSearchParams()
  const {
    urlKey,
    multiselect,
    label,
    options,
    loading,
    wrapTags = false,
  } = props
  const { t } = useT()
  const AutocompleteComponent = wrapTags ? AutocompleteWithWrap : Autocomplete
  const [idValueMap, setIdValueMap] = useState<{ [key: string]: string }>({})
  const ids = urlState[urlKey]
  const value =
    ids && isArray(ids)
      ? ids.map((id: string) => ({
          value: id,
          label: idValueMap[id] || t('autocompleteWithUrl.loading'),
        }))
      : typeof ids === 'string'
        ? {
            value: ids,
            label: idValueMap[ids] || t('autocompleteWithUrl.loading'),
          }
        : multiselect
          ? []
          : null

  const handleChange = (
    newValue:
      | { label: string; value: string }[]
      | { label: string; value: string }
      | undefined
      | null,
  ) => {
    if (!newValue) {
      handleUpdateSearchParams({
        [urlKey]: undefined,
      })
      return
    }
    if (!Array.isArray(newValue)) {
      handleUpdateSearchParams({
        [urlKey]: newValue.value,
      })
    } else {
      handleUpdateSearchParams({
        [urlKey]: newValue.map(({ value }) => value),
      })
    }
  }

  useEffect(() => {
    const idValueMapNew = options.reduce(
      (acc, { value, label }) => {
        acc[value] = label
        return acc
      },
      {} as { [key: string]: string },
    )
    setIdValueMap(idValueMapNew)
  }, [options])

  const defaultRenderOption = (
    label: string,
    state: AutocompleteRenderOptionState,
  ) => (
    <>
      {multiselect && <Checkbox sx={{ mr: 1 }} checked={state.selected} />}
      {!multiselect && <Radio sx={{ mr: 1 }} checked={state.selected} />}
      <Typography>{label}</Typography>
    </>
  )

  return (
    <AutocompleteComponent
      onChange={(_, newValue) => handleChange(newValue)}
      value={value}
      options={options}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      multiple={multiselect}
      loading={loading}
      sx={{ minWidth: 200, display: 'flex' }}
      fullWidth
      disableListWrap
      slotProps={{
        paper: {
          variant: 'outlined',
        },
      }}
      renderInput={params => (
        <TextField
          label={label}
          {...params}
          placeholder={t('autocompleteWithUrl.search')}
        />
      )}
      renderOption={(props, option, state) => (
        <ListItem
          {...props}
          data-testid={`option-${state.index}`}
          sx={theme => ({
            ':hover': {
              backgroundColor: theme.palette.grey['200'],
            },
          })}
        >
          {defaultRenderOption(option.label, state)}
        </ListItem>
      )}
    />
  )
}
